import { createEffect, createMemo, createSignal, onCleanup, Show } from "solid-js";
import { createStore } from "solid-js/store";
import {
  buildCost,
  buildSize,
  DEFAULT_CONFIG,
  type DeckBuild,
  type CardTypeId,
  type PlayerId,
} from "../../core";
import { roomLink } from "../../app/api";
import { randomDeck } from "../../app/decks";
import type { OnlineSession } from "../../app/online";
import { DeckBuilder } from "../components/DeckBuilder";
import { ThemeToggle } from "../components/ThemeToggle";
import { Icon } from "../components/Icon";

export function OnlineLobby(props: {
  roomId: string;
  session: OnlineSession;
  onExit: () => void;
}) {
  const s = props.session;
  const mySeat = (): PlayerId => s.mySeat() ?? 0;
  const otherSeat = (): PlayerId => (mySeat() === 0 ? 1 : 0);

  const [name, setName] = createSignal(`Player ${mySeat() + 1}`);
  const [build, setBuild] = createStore<DeckBuild>({});
  const [submitted, setSubmitted] = createSignal(false);
  const [copied, setCopied] = createSignal(false);

  const draftMode = () => s.mode() !== "classic"; // draft is the default
  const isHost = () => mySeat() === 0; // the room creator chooses the build mode

  const cost = () => buildCost(build);
  const canSubmit = () => draftMode() || (buildSize(build) > 0 && cost() <= DEFAULT_CONFIG.budget);

  const opponent = createMemo(() => s.seats()?.[otherSeat()] ?? null);

  // The host can switch modes until they lock in; the server clears both seats'
  // readiness when it changes, so anyone who'd already submitted reconfirms.
  createEffect((prev) => {
    const mode = s.mode();
    if (prev !== undefined && mode !== prev) setSubmitted(false);
    return mode;
  });

  function change(type: CardTypeId, count: number) {
    setBuild(type, count);
  }

  function ready() {
    if (!canSubmit()) return;
    // In draft mode the build is just a readiness marker; the real deck is
    // chosen on the draft screen once both players are ready.
    s.submitDeck(name(), draftMode() ? {} : build);
    setSubmitted(true);
  }

  // Deck creation is on the clock here too — at zero we lock in your current
  // deck (or a drafted one) so a slow builder can't stall the room forever.
  const DECK_SECONDS = 120;
  const [timeLeft, setTimeLeft] = createSignal(DECK_SECONDS);
  const timer = window.setInterval(() => {
    setTimeLeft((t) => {
      if (t > 1) return t - 1;
      window.clearInterval(timer);
      if (!submitted()) {
        if (canSubmit()) ready();
        else {
          // Classic only: lock in a random legal deck so a slow builder can't
          // stall the room. (Draft mode is always ready-able, so we never land here.)
          s.submitDeck(name(), randomDeck(DEFAULT_CONFIG.budget));
          setSubmitted(true);
        }
      }
      return 0;
    });
  }, 1000);
  onCleanup(() => window.clearInterval(timer));
  const clock = () => {
    const t = timeLeft();
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
  };

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(roomLink(props.roomId));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; the input is selectable as a fallback */
    }
  }

  return (
    <div class="app">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">online lobby</span>
        </div>
        <div class="topbar-actions">
          <ThemeToggle />
          <button class="btn btn-ghost" onClick={props.onExit}>
            Leave
          </button>
        </div>
      </div>

      <div class="setup" style={{ "max-width": "720px", margin: "0 auto" }}>
        <div class="panel">
          <h2>Invite your opponent</h2>
          <div class="share">
            <input type="text" readonly value={roomLink(props.roomId)} onFocus={(e) => e.currentTarget.select()} />
            <button class="btn btn-primary" onClick={copyLink}>
              {copied() ? "Copied!" : "Copy link"}
            </button>
          </div>

          <div class="lobby-seats">
            <div class="lobby-seat">
              <span>
                <span class="dotlive on" />
                You — seat {mySeat() + 1}
              </span>
              <span class="pill" classList={{ ok: submitted() }}>
                {submitted() ? "ready" : draftMode() ? "not ready" : "building…"}
              </span>
            </div>
            <div class="lobby-seat">
              <span>
                <span class="dotlive" classList={{ on: !!opponent()?.connected }} />
                {opponent() ? opponent()!.name : "Waiting for opponent to join…"}
              </span>
              <Show when={opponent()}>
                <span class="pill" classList={{ ok: !!opponent()?.ready }}>
                  {opponent()?.ready ? "ready" : draftMode() ? "not ready" : "building…"}
                </span>
              </Show>
            </div>
          </div>

          <div class="field" style={{ "margin-top": "12px" }}>
            <label>How you build your decks</label>
            <Show
              when={isHost()}
              fallback={
                <span class="budget-sub">
                  The host chose {draftMode() ? "Draft" : "Classic"} — {draftMode() ? "draft from a shared pool" : "build your own deck"}.
                </span>
              }
            >
              <div class="seg">
                <button classList={{ active: draftMode() }} disabled={submitted()} onClick={() => s.setMode("draft")}>
                  Draft
                </button>
                <button classList={{ active: !draftMode() }} disabled={submitted()} onClick={() => s.setMode("classic")}>
                  Classic
                </button>
              </div>
            </Show>
          </div>

          <p class="budget-sub">
            {draftMode()
              ? "Once both players are ready, you'll draft your armies from a shared pool, taking turns."
              : "The game starts automatically once both players lock in a deck. Decks stay secret — your opponent never sees your cards."}
          </p>
        </div>

        <div class="field" style={{ "max-width": "320px" }}>
          <label>Your name</label>
          <input type="text" value={name()} onInput={(e) => setName(e.currentTarget.value)} />
        </div>

        <Show
          when={!draftMode()}
          fallback={
            <div class="field-row" style={{ "justify-content": "flex-end" }}>
              <Show when={submitted()}>
                <span class="budget-sub" style={{ "align-self": "center" }}>
                  Ready — waiting for your opponent.
                </span>
              </Show>
              <button class="btn btn-primary" disabled={submitted()} onClick={ready}>
                {submitted() ? "Waiting…" : "Ready to draft"}
              </button>
            </div>
          }
        >
          <div class="field-row" style={{ gap: "8px", "justify-content": "space-between" }}>
            <strong style={{ "font-size": "13px" }}>Build your deck</strong>
            <Show when={!submitted()}>
              <span class="deck-timer" classList={{ urgent: timeLeft() <= 15 }} title="Deck creation time — your deck auto-locks at 0:00">
                <Icon name="clock" /> {clock()}
              </span>
            </Show>
          </div>

          <DeckBuilder
            owner={mySeat()}
            name={name()}
            build={build}
            config={DEFAULT_CONFIG}
            onName={setName}
            onChange={change}
          />

          <div class="field-row" style={{ "justify-content": "flex-end" }}>
            <Show when={submitted()}>
              <span class="budget-sub" style={{ "align-self": "center" }}>
                Deck locked in — waiting for opponent.
              </span>
            </Show>
            <button class="btn btn-primary" disabled={!canSubmit()} onClick={ready}>
              {submitted() ? "Update deck" : "Lock in deck"}
            </button>
          </div>
        </Show>

        <Show when={s.error}>
          <div class="error">{s.error}</div>
        </Show>
      </div>
    </div>
  );
}
